import { useEffect } from "react";
import { Box, Typography, CircularProgress } from "@mui/material";
import { useAppDispatch } from "State/hooks";
import { setExpired } from "State/subscriptionSlice";
import { useLazyGetSubscriptionStatusQuery } from "Services/subscription";

const ACTIVE_PLANS = ["pro", "growth"];

// company/subscription always reads DynamoDB live and, per the backend team, busts the
// tenant-expired guard's cache as a side effect specifically for this post-checkout case
// (see company.controller.ts getSubscription) — so it's the correct thing to poll here,
// checking the returned `plan` itself rather than treating a 200 as proof of anything (this
// endpoint stays reachable even for expired tenants). The Stripe->DynamoDB update can take
// a while, so this polls for up to 2 minutes rather than assuming a short fixed delay. A
// full window.location redirect (not client-side navigate) is used at the end so the app
// cold-boots with a clean RTK Query cache rather than reusing entries cached while expired.
const POLL_INTERVAL_MS = 2000;
const MAX_POLL_ATTEMPTS = 60;

export default function SubscribeSuccess() {
  const dispatch = useAppDispatch();
  const [triggerGetStatus] = useLazyGetSubscriptionStatusQuery();

  useEffect(() => {
    dispatch(setExpired(false));
    let cancelled = false;

    const waitForActiveSubscription = async () => {
      for (let attempt = 0; attempt < MAX_POLL_ATTEMPTS; attempt++) {
        const result = await triggerGetStatus(undefined, false);
        if (result.data && ACTIVE_PLANS.includes(result.data.plan)) {
          dispatch(setExpired(false));
          break;
        }
        if (cancelled) return;
        await new Promise((resolve) => setTimeout(resolve, POLL_INTERVAL_MS));
      }
      if (!cancelled) window.location.href = "/home";
    };

    waitForActiveSubscription();
    return () => {
      cancelled = true;
    };
  }, [dispatch, triggerGetStatus]);

  return (
    <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" minHeight="60vh" gap={2}>
      <CircularProgress />
      <Typography variant="h5" fontWeight={700}>
        Subscription confirmed
      </Typography>
      <Typography color="text.secondary">Finishing setup, this'll just take a moment...</Typography>
    </Box>
  );
}